import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Box, Typography, IconButton, Dialog, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import CloseIcon from '@mui/icons-material/Close';
import { loadLesson, identifyStepChord, shortestTransposition, transposeSteps, stepsOfSection } from '../utils/lessonSteps';
import { useLessonEngine, LESSON_SPEEDS } from '../hooks/useLessonEngine';
import { publishLessonNeck, clearLessonNeck } from '../hooks/useLessonNeck';
import { getGuitarTuning } from '../utils/guitarNeckUtils';
import LessonPanel from './LessonPanel';
import { traceInput, traceStepCommit } from '../utils/lessonPerfTrace';
import { noLessonAnimations } from '../utils/perfVariant';

const LESSON_COLOR = 'var(--hl-root)';
const DEFAULT_TEMPO = 120;
// Qualites pour lesquelles identifyStepChord rend des degres d'accord fiables.
const CHORD_QUALITIES = ['major', 'minor', 'diminished', 'augmented'];
// Meme cache-bust que BackingTrackPlayer : le JSON est reecrit au meme chemin a
// chaque reconversion cote Admin, updatedAt change l'URL et force un fetch frais.
const withCacheBust = (url, version) => {
  if (!url || !version) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(version)}`;
};

// Surcharge Admin des sections : un tableau non vide remplace celles du JSON, et le
// sectionIndex de chaque pas est recalcule depuis les nouvelles plages (le moteur
// boucle sur lesson.sections[step.sectionIndex]). Null, {} ou [] = sections du JSON.
const applySections = (lesson, override) => {
  if (!Array.isArray(override) || override.length === 0) return lesson;
  const sections = override.map((section) => ({ ...section }));
  const steps = lesson.steps.map((step, i) => ({
    ...step,
    sectionIndex: sections.findIndex((s) => i >= s.firstStep && i <= s.lastStep)
  }));
  return { ...lesson, sections, steps };
};

// Le JSON compte les cordes depuis le mi aigu (string 0 = corde 1), le manche
// depuis la corde grave (stringIndex 0 = mi grave, getGuitarTuning() est listee
// grave -> aigu). Une lecon 6 cordes tombe sur les cordes aigues d'un manche 7 cordes.
const toNeckStringIndex = (string) => getGuitarTuning().length - 1 - string;

const noteKey = (stringIndex, fret) => `${stringIndex}:${fret}`;

const formatSpeed = (speed) => `x${speed}`;

const blurOnPointer = (e) => e.currentTarget.blur();

const roundButton = (background, size) => ({
  width: size,
  height: size,
  borderRadius: '50%',
  backgroundColor: background,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexShrink: 0,
  transition: 'transform 0.2s ease',
  border: 0,
  padding: 0,
  font: 'inherit',
  '&:hover': { transform: 'scale(1.05)' },
  '&:active': { transform: 'scale(0.95)' },
  '&:focus-visible': { outline: `3px solid ${LESSON_COLOR}`, outlineOffset: 2 }
});

const transportButton = (width, background) => ({
  width,
  height: 44,
  minHeight: 44,
  borderRadius: '22px',
  appearance: 'none',
  border: 0,
  padding: 0,
  backgroundColor: background,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexShrink: 0,
  userSelect: 'none',
  font: 'inherit',
  transition: 'transform 0.2s ease',
  '&:hover': { transform: 'scale(1.05)' },
  '&:active': { transform: 'scale(0.95)' },
  '&:disabled': { opacity: 0.35, cursor: 'default', transform: 'none' },
  '&:disabled:hover': { transform: 'none' },
  '&:focus-visible': { outline: `3px solid ${LESSON_COLOR}`, outlineOffset: 2 }
});

const barContainer = {
  backgroundColor: 'rgba(30, 30, 40, 0.8)',
  borderRadius: '20px',
  overflow: 'hidden',
  width: 'calc(100% - 32px)',
  maxWidth: '1470px',
  mx: 'auto',
  mb: 2,
  'body.compact-mode &': {
    width: 'calc(100% - 92px)',
    ml: 2,
    mr: '76px'
  }
};

const dimText = { color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' };

/**
 * LessonPlayer - la barre du lecteur de lecon (remplace NoteGrid quand une lecon
 * est active) et son panneau (intro + sections, LessonPanel). Le panneau s'ouvre
 * a chaque entree des que le JSON est charge. Charge le JSON de pas, le transpose sur
 * la tonique de Play si la lecon est transposable, et publie dans useLessonNeck les
 * notes de la section courante (current: false) et celles du pas courant (current: true).
 */
const LessonPlayer = forwardRef(({
  lesson,
  artist,
  rootNote,
  currentMode,
  onClose,
  onPlayingStateChange,
  onTempoChange
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isPhonePortrait = useMediaQuery(`${theme.breakpoints.down('sm')} and (orientation: portrait)`);
  const [json, setJson] = useState(null);
  const [error, setError] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  const callbacksRef = useRef({});
  callbacksRef.current = { onPlayingStateChange, onTempoChange };

  useEffect(() => {
    let cancelled = false;
    setJson(null);
    setError(null);
    fetch(withCacheBust(lesson.jsonUrl, lesson.updatedAt))
      .then((res) => {
        if (!res.ok) throw new Error(`lesson: HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setJson(loadLesson(data));
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });
    return () => { cancelled = true; };
  }, [lesson.jsonUrl, lesson.updatedAt]);

  // Le panneau s'ouvre des que le JSON est la (pas pendant le chargement) : il
  // dit ou on est avant le premier geste. Cle sur json, pas sur readyLesson, qui
  // change aussi a chaque transposition.
  useEffect(() => {
    if (json) setPanelOpen(true);
  }, [json]);

  const lessonRoot = json ? (json.rootNote ?? lesson.rootNote) : null;

  const readyLesson = useMemo(() => {
    if (!json) return null;
    let steps = json.steps;
    if (lesson.transposable && lessonRoot && rootNote) {
      const semitones = shortestTransposition(lessonRoot, rootNote);
      if (semitones !== 0) steps = transposeSteps(json.steps, semitones);
    }
    return applySections({ ...json, steps }, lesson.sections);
  }, [json, rootNote, lesson.transposable, lessonRoot, lesson.sections]);

  const engine = useLessonEngine(readyLesson, { tempo: lesson.tempo || undefined });
  const { currentStep, currentStepIndex, currentSectionIndex, atSectionStart, sections, playing, speed, pendingPlay, pianoState } = engine;
  // Les fonctions du moteur sont stables : en dépendre plutôt que de l'objet
  // évite de recréer les callbacks et l'écouteur clavier à chaque pas.
  const { pause, setSpeed, jumpToSection, stepBack, stepForward, toggle, stop } = engine;

  // Notes de la section courante, dedupliquees par case du manche. Memoisees par
  // section : une lecon de 2000 pas ne les recalcule pas a chaque pas.
  const sectionNotes = useMemo(() => {
    if (!readyLesson || currentSectionIndex < 0) return [];
    const seen = new Set();
    const notes = [];
    stepsOfSection(readyLesson, currentSectionIndex).forEach((step) => {
      step.notes.forEach((n) => {
        const stringIndex = toNeckStringIndex(n.string);
        const key = noteKey(stringIndex, n.fret);
        if (seen.has(key)) return;
        seen.add(key);
        notes.push({ stringIndex, fret: n.fret, current: false });
      });
    });
    return notes;
  }, [readyLesson, currentSectionIndex]);

  useEffect(() => {
    if (!json) return;
    const { onTempoChange: notify } = callbacksRef.current;
    if (notify) notify(lesson.tempo || json.tempo || DEFAULT_TEMPO);
  }, [json, lesson.tempo]);

  const stepIndexRef = useRef(currentStepIndex);
  stepIndexRef.current = currentStepIndex;

  // Ce que le manche doit afficher, calcule au rendu plutot que dans un effet :
  // le magasin le publie ensuite, et seul l'adaptateur du manche se rend.
  const { notes, lowestFret, chordName } = useMemo(() => {
    if (!currentStep) {
      // Pas 0 : la section reste affichee en niveau 3, aucun carre cible, et
      // aucune frette a viser (le manche ne defile pas). Chargement ou erreur :
      // null, le manche reste en mode normal.
      return { notes: sectionNotes.length ? sectionNotes : null, lowestFret: null, chordName: '' };
    }
    const chord = identifyStepChord(currentStep, { rootNote, mode: currentMode });
    // Le chiffre porte par la note est un degre d'ACCORD : il n'a de sens que
    // sur une triade identifiee. Une lecon de gamme (un pas = une note) ou un
    // empilement non triadique laisse le manche afficher le degre de gamme.
    const hasChordDegrees = CHORD_QUALITIES.includes(chord.quality);
    const stepNotes = currentStep.notes.map((n) => ({
      stringIndex: toNeckStringIndex(n.string),
      fret: n.fret,
      label: hasChordDegrees ? chord.degrees[`${n.string}:${n.fret}`] : undefined,
      current: true
    }));
    const stepKeys = new Set(stepNotes.map((n) => noteKey(n.stringIndex, n.fret)));
    return {
      notes: sectionNotes
        .filter((n) => !stepKeys.has(noteKey(n.stringIndex, n.fret)))
        .concat(stepNotes),
      lowestFret: Math.min(...currentStep.notes.map((n) => n.fret)),
      chordName: chord.name
    };
  }, [currentStep, sectionNotes, rootNote, currentMode]);

  // La trace horodate le commit du lecteur, comme avant le point 2 : elle reste
  // comparable aux mesures deja prises et ne voit pas le rendu du manche, qui
  // part en voie synchrone depuis la publication ci-dessous.
  useEffect(() => {
    traceStepCommit(currentStep ? stepIndexRef.current : -1);
  }, [currentStep, sectionNotes, rootNote, currentMode]);

  useLayoutEffect(() => {
    publishLessonNeck(notes, lowestFret);
  }, [notes, lowestFret]);

  // Sortie de lecon : le manche redevient un manche normal.
  useLayoutEffect(() => clearLessonNeck, []);

  useEffect(() => {
    const { onPlayingStateChange: notify } = callbacksRef.current;
    if (notify) notify(playing);
  }, [playing]);

  const openVideo = useCallback(() => {
    pause();
    setVideoOpen(true);
  }, [pause]);

  const closeVideo = useCallback(() => setVideoOpen(false), []);

  const cycleSpeed = useCallback(() => {
    const idx = LESSON_SPEEDS.indexOf(speed);
    setSpeed(LESSON_SPEEDS[(idx + 1) % LESSON_SPEEDS.length]);
  }, [setSpeed, speed]);

  // Ouvrir le panneau met le moteur en pause, comme la video : on ne choisit
  // pas une section pendant que la boucle avance.
  const openPanel = useCallback(() => {
    pause();
    setPanelOpen(true);
  }, [pause]);

  const closePanel = useCallback(() => setPanelOpen(false), []);

  const pickSection = useCallback((index) => {
    pause();
    jumpToSection(index);
    setPanelOpen(false);
  }, [pause, jumpToSection]);

  const startLesson = useCallback(() => pickSection(0), [pickSection]);

  // La trace d'entrée relie le geste au moment où la note est programmée.
  const onPrevStep = useCallback((e) => {
    traceInput(e && e.timeStamp);
    stepBack();
  }, [stepBack]);

  const onNextStep = useCallback((e) => {
    traceInput(e && e.timeStamp);
    stepForward();
  }, [stepForward]);

  const goPrevSection = useCallback(() => {
    jumpToSection(currentSectionIndex - 1);
  }, [jumpToSection, currentSectionIndex]);

  const goNextSection = useCallback(() => {
    jumpToSection(currentSectionIndex + 1);
  }, [jumpToSection, currentSectionIndex]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (panelOpen || videoOpen || e.isComposing) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target;
      const tag = (target && target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
      if (target?.closest?.('input, textarea, select, [contenteditable="true"], [role="slider"]')) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        traceInput(e.timeStamp);
        stepForward();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        traceInput(e.timeStamp);
        stepBack();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        goNextSection();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        goPrevSection();
      } else if (e.key === ' ' || e.code === 'Space') {
        if (e.repeat) return;
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [stepForward, stepBack, toggle, goPrevSection, goNextSection, panelOpen, videoOpen]);

  useImperativeHandle(ref, () => ({
    stepForward,
    stepBack,
    pause,
    // Retour Android : ferme la couche la plus haute (video, puis panneau) et rend
    // true, sinon false et App sort de la lecon.
    handleBack: () => {
      if (videoOpen) { setVideoOpen(false); return true; }
      if (panelOpen) { setPanelOpen(false); return true; }
      return false;
    }
  }), [stepForward, stepBack, pause, videoOpen, panelOpen]);

  const currentSection = currentSectionIndex >= 0 ? sections[currentSectionIndex] : null;
  const stepCount = readyLesson ? readyLesson.steps.length : 0;
  const stepPosition = atSectionStart
    ? 0
    : currentSection
      ? Math.max(1, currentStepIndex - currentSection.firstStep + 1)
      : currentStepIndex + 1;
  const stepTotal = currentSection
    ? currentSection.lastStep - currentSection.firstStep + 1
    : stepCount;
  const atFirstSection = currentSectionIndex <= 0;
  const atLastSection = currentSectionIndex < 0 || currentSectionIndex >= sections.length - 1;
  const atFirstStep = atSectionStart && currentSectionIndex <= 0;
  const atLastStep = !atSectionStart && currentStepIndex >= stepCount - 1;

  const iconButtonSx = {
    color: 'rgba(255,255,255,0.7)',
    width: 44,
    height: 44,
    p: isMobile ? 0.5 : 0.625,
    '&:focus-visible': { outline: `3px solid ${LESSON_COLOR}`, outlineOffset: 2 }
  };
  const edgeInset = isMobile ? 0.5 : 0;
  const playSize = 44;
  // État de préparation du piano : attente pendant le décodage des samples,
  // piano indisponible après un échec, le clic relance le chargement.
  const playLabel = pendingPlay
    ? 'Loading piano'
    : pianoState === 'error'
      ? 'Piano unavailable, tap to retry'
      : playing ? 'Pause' : 'Play';

  // Variante B/D du point 5 : la barre perd ses transitions (mise à l'échelle des
  // boutons), rien d'autre ne change.
  const barClass = noLessonAnimations() ? 'neck-reduced-motion' : undefined;

  if (error) {
    return (
      <Box className={barClass} sx={barContainer}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 1, gap: 0.5, minHeight: 60 }}>
          <IconButton size="small" onClick={onClose} aria-label="Back" sx={iconButtonSx}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography sx={dimText}>Could not load this lesson.</Typography>
        </Box>
      </Box>
    );
  }

  if (!readyLesson) {
    return (
      <Box className={barClass} sx={barContainer}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 1, gap: 1, minHeight: 60 }}>
          <IconButton size="small" onClick={onClose} aria-label="Back" sx={iconButtonSx}>
            <ChevronLeftIcon />
          </IconButton>
          <CircularProgress size={18} sx={{ color: LESSON_COLOR }} />
          <Typography sx={dimText}>Loading lesson...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={barClass} sx={barContainer}>
      <Box data-testid="lesson-player-layout" sx={isPhonePortrait ? {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gridTemplateRows: 'auto auto',
        alignItems: 'center',
        columnGap: 0.5,
        rowGap: 0.5,
        px: 1,
        py: 0.75
      } : {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
        alignItems: 'center',
        columnGap: 1,
        px: 1,
        py: 1,
        minHeight: 60
      }}>
        {/* Gauche : retour, section (panneau), accord et position dans la section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.25 : 0.5, minWidth: 0, flex: 1, gridColumn: isPhonePortrait ? '1' : 'auto', gridRow: isPhonePortrait ? '1' : 'auto' }}>
          <IconButton size="small" onClick={onClose} aria-label="Back" sx={{ ...iconButtonSx, ml: -edgeInset }}>
            <ChevronLeftIcon />
          </IconButton>

          <Box
            component="button"
            type="button"
            aria-label="Sections"
            onClick={openPanel}
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 44,
              p: 0,
              border: 0,
              background: 'none',
              font: 'inherit',
              textAlign: 'left',
              color: 'inherit',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              '&:focus-visible': { outline: `3px solid ${LESSON_COLOR}`, outlineOffset: 2, borderRadius: 1 }
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{
                fontSize: '13px',
                fontWeight: 'bold',
                color: 'white',
                overflow: isPhonePortrait ? 'visible' : 'hidden',
                textOverflow: isPhonePortrait ? 'clip' : 'ellipsis',
                whiteSpace: isPhonePortrait ? 'normal' : 'nowrap',
                lineHeight: 1.3
              }}>
                {currentSection ? currentSection.name : lesson.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, whiteSpace: 'nowrap', lineHeight: 1.3, minWidth: 0, overflow: 'hidden' }}>
                {chordName && (
                  <Typography component="span" sx={{ fontSize: '12px', color: LESSON_COLOR, fontWeight: 'bold' }}>
                    {chordName}
                  </Typography>
                )}
                <Typography
                  component="span"
                  data-testid="lesson-step-counter"
                  sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', fontVariantNumeric: 'tabular-nums' }}
                >
                  {stepPosition} / {stepTotal}
                </Typography>
              </Box>
            </Box>
            <KeyboardArrowDownIcon sx={{ fontSize: isMobile ? 16 : 18, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }} />
          </Box>
        </Box>

        {/* Centre : le transport, du saut de section au pas, Next step en tete d'affiche */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexShrink: 0,
          gridColumn: isPhonePortrait ? '1 / -1' : 'auto',
          gridRow: isPhonePortrait ? '2' : 'auto',
          justifyContent: 'center'
        }}>
          <Box
            component="button"
            type="button"
            aria-label="Previous section"
            onMouseUp={blurOnPointer}
            onClick={goPrevSection}
            disabled={atFirstSection}
            sx={transportButton(44, 'rgba(255,255,255,0.08)')}
          >
            <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 22, color: 'rgba(255,255,255,0.85)' }} />
          </Box>

          <Box
            component="button"
            type="button"
            aria-label="Previous step"
            onMouseUp={blurOnPointer}
            onClick={onPrevStep}
            disabled={atFirstStep}
            sx={transportButton(44, 'rgba(255,255,255,0.08)')}
          >
            <SkipPreviousIcon sx={{ fontSize: 22, color: 'rgba(255,255,255,0.85)' }} />
          </Box>

          <Box
            component="button"
            type="button"
            aria-label="Stop"
            onMouseUp={blurOnPointer}
            onClick={stop}
            sx={transportButton(44, 'rgba(255,255,255,0.08)')}
          >
            <StopIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.85)' }} />
          </Box>

          <Box
            component="button"
            type="button"
            onClick={toggle}
            onMouseUp={blurOnPointer}
            aria-label={playLabel}
            sx={{ ...roundButton('rgba(255,255,255,0.12)', playSize), opacity: pianoState === 'error' ? 0.5 : 1 }}
          >
            {pendingPlay ? (
              <CircularProgress size={18} sx={{ color: 'white' }} />
            ) : playing ? (
              <PauseIcon sx={{ fontSize: 22, color: 'white' }} />
            ) : (
              <PlayArrowIcon sx={{ fontSize: 22, color: 'white', ml: 0.3 }} />
            )}
          </Box>

          <Box
            component="button"
            type="button"
            aria-label="Next step"
            onMouseUp={blurOnPointer}
            onClick={onNextStep}
            disabled={atLastStep}
            sx={transportButton(56, 'white')}
          >
            <SkipNextIcon sx={{ fontSize: 24, color: 'black' }} />
          </Box>

          <Box
            component="button"
            type="button"
            aria-label="Next section"
            onMouseUp={blurOnPointer}
            onClick={goNextSection}
            disabled={atLastSection}
            sx={transportButton(44, 'rgba(255,255,255,0.08)')}
          >
            <KeyboardDoubleArrowRightIcon sx={{ fontSize: 22, color: 'rgba(255,255,255,0.85)' }} />
          </Box>
        </Box>

        {/* Droite : vitesse et video */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.25 : 0.5, flexShrink: 0, justifySelf: 'end', gridColumn: isPhonePortrait ? '2' : 'auto', gridRow: isPhonePortrait ? '1' : 'auto' }}>
          <Box
            component="button"
            type="button"
            onClick={cycleSpeed}
            onMouseUp={blurOnPointer}
            aria-label="Speed"
            sx={{
              minWidth: 44,
              height: 44,
              px: 0.5,
              borderRadius: '22px',
              border: 0,
              backgroundColor: speed === 1 ? 'rgba(255,255,255,0.1)' : 'rgba(var(--hl-root-rgb), 0.2)',
              color: speed === 1 ? 'rgba(255,255,255,0.85)' : LESSON_COLOR,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
              fontSize: '12px',
              fontWeight: 'bold',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
              userSelect: 'none',
              '&:focus-visible': { outline: `3px solid ${LESSON_COLOR}`, outlineOffset: 2 }
            }}
          >
            {formatSpeed(speed)}
          </Box>

          {lesson.videoUrl && (
            <IconButton size="small" onClick={openVideo} aria-label="Video" sx={{ ...iconButtonSx, color: 'rgba(255,255,255,0.85)', mr: -edgeInset }}>
              <OndemandVideoIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <LessonPanel
        open={panelOpen}
        lesson={lesson}
        artist={artist}
        sections={sections}
        currentSectionIndex={currentSectionIndex}
        onClose={closePanel}
        onStart={startLesson}
        onSelectSection={pickSection}
        onWatchVideo={openVideo}
      />

      {lesson.videoUrl && (
        <Dialog
          open={videoOpen}
          onClose={closeVideo}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#141420',
              backgroundImage: 'none',
              borderRadius: '16px',
              m: 1
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: '1rem', fontWeight: 'bold', color: 'white' }}>
                {lesson.title}
              </Typography>
              {artist && (
                <Typography noWrap sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
                  {artist.name}
                </Typography>
              )}
            </Box>
            <IconButton onClick={closeVideo} aria-label="Close video" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ backgroundColor: 'black' }}>
            <video
              data-testid="lesson-video"
              src={lesson.videoUrl}
              controls
              playsInline
              autoPlay
              style={{ display: 'block', width: '100%', maxHeight: '70vh' }}
            />
          </Box>
        </Dialog>
      )}
    </Box>
  );
});

export default LessonPlayer;
